import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@chapche/ui';
import Title from '../Title';
import Loading from '../Loading';
import { SearchIcon } from '../../appearence/icons/search_icon';
import {
    SvgAssetListItem,
    svgAssetsService,
    svgPreviewDataUrl,
} from '../../services/svgAssetsService';
import { errorCallback } from '../../util';

const SvgLibraryPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState<SvgAssetListItem[]>([]);
    const [tagCatalog, setTagCatalog] = useState<string[]>([]);
    const [q, setQ] = useState('');
    const [appliedQ, setAppliedQ] = useState('');
    const [tag, setTag] = useState('');

    const load = useCallback(async (query: string, tagFilter: string) => {
        setLoading(true);
        try {
            const list = await svgAssetsService.list({
                q: query || undefined,
                tag: tagFilter || undefined,
            });
            setItems(list);
            if (!query && !tagFilter) {
                const tags = new Set<string>();
                list.forEach((item) => (item.tags || []).forEach((tg) => tags.add(tg)));
                setTagCatalog([...tags].sort());
            }
        } catch (e) {
            errorCallback(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load(appliedQ, tag);
    }, [appliedQ, tag, load]);

    const onSearch = (e: FormEvent) => {
        e.preventDefault();
        setAppliedQ(q.trim());
    };

    const allTags = useMemo(() => {
        if (tagCatalog.length) return tagCatalog;
        const set = new Set<string>();
        items.forEach((item) => (item.tags || []).forEach((tg) => set.add(tg)));
        return [...set].sort();
    }, [tagCatalog, items]);

    const empty = useMemo(() => !loading && items.length === 0, [loading, items.length]);

    return (
        <section className="main__content workspace flex-col">
            <div className="product__header w-full">
                <Title titleValue={t('app.page.svg-library.title')} />
                <Button variant="outline" onClick={() => navigate('/svg-library/new')}>
                    {t('app.page.svg-library.new')}
                </Button>
            </div>
            <p className="block__content w-full">{t('app.page.svg-library.hint')}</p>
            <form className="mt-16 mb-12 flex w-full max-w-[560px] items-center gap-12" onSubmit={onSearch}>
                <div className="relative min-w-0 flex-1">
                    <SearchIcon nameOfClass="product__input-icon pointer-events-none absolute top-1/2 left-16 h-20 w-20 -translate-y-1/2" />
                    <Input
                        fullWidth
                        className="pl-40"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={t('app.page.svg-library.searchPlaceholder')}
                    />
                </div>
                <Button type="submit" variant="outline">
                    {t('app.page.svg-library.search')}
                </Button>
            </form>
            {allTags.length > 0 || tag ? (
                <div className="mb-20 flex w-full flex-wrap gap-8">
                    <Button
                        size="small"
                        variant={tag === '' ? 'default' : 'outline'}
                        onClick={() => setTag('')}
                    >
                        {t('app.page.svg-library.allTags')}
                    </Button>
                    {allTags.map((tg) => (
                        <Button
                            key={tg}
                            size="small"
                            variant={tag === tg ? 'default' : 'outline'}
                            onClick={() => setTag(tag === tg ? '' : tg)}
                        >
                            {tg}
                        </Button>
                    ))}
                </div>
            ) : null}
            {loading ? <Loading /> : null}
            {empty ? <p className="block__content w-full">{t('app.page.svg-library.empty')}</p> : null}
            <ul className="m-0 grid w-full list-none grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-16 p-0">
                {items.map((item) => (
                    <li key={item.id}>
                        <Link to={`/svg-library/${item.id}/edit`} className="block text-ink no-underline">
                            <div className="flex aspect-square items-center justify-center overflow-hidden border border-solid border-border bg-light-beige [&_img]:h-full [&_img]:w-full [&_img]:object-contain">
                                {item.svgText ? (
                                    <img src={svgPreviewDataUrl(item.svgText)} alt={item.title} />
                                ) : (
                                    <span className="block__content">{item.title}</span>
                                )}
                            </div>
                            <div className="mt-8 flex flex-col gap-4 text-m">
                                <strong>{item.title}</strong>
                                <span>
                                    {new Date(item.updatedAt).toLocaleString()}
                                    {' · '}
                                    {Math.ceil(item.byteSizeSvg / 1024)} KB
                                    {item.tags?.length ? ` · ${item.tags.join(', ')}` : ''}
                                </span>
                            </div>
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default SvgLibraryPage;
